import React, { useState, useEffect, useMemo } from 'react';
import { getMyPaymentHistory, getMyCoursePurchases } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { 
  CreditCard, 
  Calendar, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  History,
  TrendingUp,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Download
} from 'lucide-react';
import PageLoader from '../../components/PageLoader';

const UserPaymentHistory = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('subscription');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const [planData, courseData] = await Promise.all([
          getMyPaymentHistory(),
          getMyCoursePurchases()
        ]);
        
        const plans = planData.map(p => ({ ...p, itemType: 'plan' }));
        const courses = (courseData?.purchases || []).map(c => ({ ...c, itemType: 'course' }));
        
        const combined = [...plans, ...courses].sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        
        setHistory(combined);
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || 'Failed to fetch payment history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const getStatusColor = (status, isLive) => {
    if (status === 'captured') {
      return isLive ? 'text-emerald-600 bg-emerald-50 border-emerald-100' : 'text-gray-600 bg-gray-50 border-gray-100';
    }
    return 'text-red-600 bg-red-50 border-red-100';
  };

  const checkIsLive = (payment) => {
    if (payment.status !== 'captured') return false;
    if (payment.itemType === 'course') return true; 
    const createdAt = new Date(payment.createdAt);
    const now = new Date();
    const durationDays = payment.billingCycle === 'annually' ? 365 : 30;
    const expiryDate = new Date(createdAt.getTime() + durationDays * 24 * 60 * 60 * 1000);
    return now <= expiryDate;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAmount = (paise) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(paise / 100);
  };

  const filteredHistory = useMemo(() => {
    return history.filter(p => p.itemType === (activeTab === 'subscription' ? 'plan' : 'course'));
  }, [history, activeTab]);

  const latestPlanIndexInFullHistory = useMemo(() => {
    // We need to find the latest successful plan in the FULL history to determine its active status correctly
    return history.findIndex(p => p.status === 'captured' && p.itemType === 'plan');
  }, [history]);

  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const currentData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredHistory.slice(start, start + itemsPerPage);
  }, [filteredHistory, currentPage]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleDownloadReceipt = (payment) => {
    const totalAmount = payment.amountPaise / 100;
    const gstRate = 0.18;
    const baseAmount = totalAmount / (1 + gstRate);
    const totalGst = totalAmount - baseAmount;
    const cgst = totalGst / 2;
    const sgst = totalGst / 2;

    const formatCurrency = (amt) => {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2
      }).format(amt);
    };

    const receiptHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${payment.razorpayPaymentId || 'Payment'}</title>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap" rel="stylesheet">
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Inter', sans-serif; padding: 40px; color: #1e293b; background: #f8fafc; line-height: 1.5; }
            .receipt-box { max-width: 800px; margin: auto; border: 1px solid #e2e8f0; padding: 60px; border-radius: 32px; background: white; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1); position: relative; overflow: hidden; }
            .receipt-box::before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 8px; background: linear-gradient(to right, #7c3aed, #ec4899); }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 60px; }
            .logo-container { display: flex; align-items: center; gap: 12px; }
            .logo-icon { width: 40px; height: 40px; background: #7c3aed; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 900; font-size: 20px; }
            .logo-text { font-size: 24px; font-weight: 900; color: #0f172a; letter-spacing: -0.025em; }
            .logo-text span { color: #7c3aed; }
            .receipt-badge { background: #f0fdf4; color: #166534; padding: 6px 16px; border-radius: 100px; font-size: 12px; font-weight: 700; text-transform: uppercase; border: 1px solid #dcfce7; }
            .receipt-title { font-size: 40px; font-weight: 900; margin-bottom: 40px; color: #0f172a; letter-spacing: -0.05em; }
            
            .info-section { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-bottom: 60px; }
            .info-block h4 { font-size: 11px; color: #64748b; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 12px; }
            .info-block p { font-size: 15px; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
            .info-block span { font-size: 13px; color: #64748b; font-weight: 500; }

            .item-table { width: 100%; border-collapse: collapse; margin-bottom: 40px; }
            .item-table th { text-align: left; border-bottom: 2px solid #f1f5f9; padding: 16px 0; font-size: 11px; color: #64748b; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; }
            .item-table td { padding: 24px 0; border-bottom: 1px solid #f1f5f9; }
            
            .summary-table { width: 300px; margin-left: auto; margin-bottom: 60px; }
            .summary-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
            .summary-row.total { border-top: 2px solid #f1f5f9; margin-top: 16px; padding-top: 16px; font-size: 24px; font-weight: 900; color: #0f172a; }
            .summary-label { color: #64748b; font-weight: 600; }
            .summary-value { font-weight: 700; color: #1e293b; }

            .footer { margin-top: 80px; text-align: center; border-top: 1px solid #f1f5f9; padding-top: 40px; }
            .footer p { font-size: 14px; color: #64748b; font-weight: 500; }
            .company-info { margin-top: 20px; font-size: 12px; color: #94a3b8; font-weight: 600; line-height: 1.8; }
            
            @media print { 
              body { background: white; padding: 0; }
              .receipt-box { box-shadow: none; border: none; padding: 40px; }
              .no-print { display: none; }
            }
            .no-print-btn { position: fixed; bottom: 40px; right: 40px; background: #0f172a; color: white; padding: 16px 32px; border-radius: 16px; font-weight: 800; cursor: pointer; border: none; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2); transition: all 0.2s; z-index: 100; }
            .no-print-btn:hover { transform: translateY(-2px); background: #7c3aed; }
          </style>
        </head>
        <body>
          <div class="receipt-box">
            <div class="header">
              <div class="logo-container">
                <img src="${window.location.origin}/assets/images/logo.png" alt="Logo" style="height: 50px; width: auto;" onerror="this.style.display='none'; document.getElementById('logo-fallback').style.display='flex';">
                <div id="logo-fallback" class="logo-icon" style="display: none;">M</div>
                <div class="logo-text">MARKET<span>MAVERICKS</span></div>
              </div>
              <div class="receipt-badge">Paid Successfully</div>
            </div>
            
            <div class="receipt-title">Tax Invoice</div>
            
            <div class="info-section">
              <div class="info-block">
                <h4>Customer Details</h4>
                <p>${(`${user?.first_name || ""} ${user?.last_name || ""}`).trim() || user?.name || 'Valued Customer'}</p>
                <span>${user?.email || 'N/A'}</span>
              </div>
              <div class="info-block">
                <h4>Invoice Details</h4>
                <p>#INV-${payment.razorpayPaymentId?.slice(-6) || Date.now().toString().slice(-6)}</p>
                <span>Date: ${formatDate(payment.createdAt)}</span><br/>
                <span>Payment ID: ${payment.razorpayPaymentId || 'N/A'}</span>
              </div>
            </div>

            <table class="item-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th style="text-align: right;">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <p style="font-weight: 700; color: #0f172a;">${payment.planName || payment.courseTitle}</p>
                    <p style="font-size: 12px; color: #64748b; margin-top: 4px;">${payment.itemType === 'plan' ? 'Subscription Plan' : 'Professional Course Access'}</p>
                  </td>
                  <td style="text-align: right; font-weight: 700;">${formatCurrency(baseAmount)}</td>
                </tr>
              </tbody>
            </table>

            <div class="summary-table">
              <div class="summary-row">
                <span class="summary-label">Taxable Amount</span>
                <span class="summary-value">${formatCurrency(baseAmount)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">CGST (9%)</span>
                <span class="summary-value">${formatCurrency(cgst)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">SGST (9%)</span>
                <span class="summary-value">${formatCurrency(sgst)}</span>
              </div>
              <div class="summary-row total">
                <span class="summary-label" style="color: #0f172a;">Total</span>
                <span class="summary-value">${formatCurrency(totalAmount)}</span>
              </div>
            </div>

            <div class="footer">
              <p>Thank you for being a part of Market Mavericks!</p>
              <div class="company-info">
                MARKET MAVERICKS PVT LTD<br/>
                GSTIN: 27AAECM1234F1Z5 (Mock)<br/>
                Support: help@marketmavericks.com | Web: www.marketmavericks.com
              </div>
              <p style="margin-top: 40px; font-size: 10px; color: #cbd5e1; font-weight: 400;">
                This is a computer-generated tax invoice. No physical signature is required.
              </p>
            </div>
          </div>
          <button class="no-print-btn no-print" onclick="window.print()">Download Invoice as PDF</button>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(receiptHtml);
    printWindow.document.close();
  };

  if (loading) return <PageLoader />;

  return (
    <div className="max-w-6xl 4xl:max-w-10xl mx-auto px-4 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <History className="text-primary-600" size={32} />
            Payment History
          </h1>
          <p className="text-gray-500 font-medium mt-1">
            View and manage your past subscription and course payments.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex p-1 bg-gray-100 rounded-2xl border border-gray-200 self-start">
          <button
            onClick={() => handleTabChange('subscription')}
            className={`flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-xl transition-all ${
              activeTab === 'subscription' 
                ? 'bg-white text-primary-600 shadow-sm' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <TrendingUp size={16} />
            Subscription
          </button>
          <button
            onClick={() => handleTabChange('course')}
            className={`flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-xl transition-all ${
              activeTab === 'course' 
                ? 'bg-white text-primary-600 shadow-sm' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <BookOpen size={16} />
            Courses
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-8 p-4 rounded-2xl bg-red-50 border border-red-100 text-red-700 flex items-center gap-3">
          <AlertCircle size={20} className="shrink-0" />
          <span className="font-bold text-sm">{error}</span>
        </div>
      )}

      {filteredHistory.length === 0 ? (
        <div className="bg-white rounded-[2rem] border-2 border-dashed border-gray-200 p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-50 text-gray-400 mb-4">
            {activeTab === 'subscription' ? <CreditCard size={32} /> : <BookOpen size={32} />}
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No {activeTab} history</h3>
          <p className="text-gray-500 max-w-sm mx-auto">
            You haven't made any {activeTab} payments yet.
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-[2rem] border-2 border-gray-100 shadow-sm overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">
                      {activeTab === 'subscription' ? 'Plan Details' : 'Course Details'}
                    </th>
                    <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">Amount</th>
                    <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">Date</th>
                    <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">Status</th>
                    <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest text-right">Access</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {currentData.map((payment) => {
                    // Find actual index in global history to correctly identify latest plan
                    const globalIndex = history.findIndex(p => p._id === payment._id);
                    const isLivePlan = payment.itemType === 'plan' && globalIndex === latestPlanIndexInFullHistory && checkIsLive(payment);
                    const isLiveCourse = payment.itemType === 'course' && payment.status === 'captured';
                    const isLive = isLivePlan || isLiveCourse;

                    return (
                      <tr key={payment._id} className="hover:bg-gray-50/30 transition-colors group">
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-xl ${payment.status === 'captured' ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-400'}`}>
                              {payment.itemType === 'plan' ? <TrendingUp size={18} /> : <BookOpen size={18} />}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900">{payment.planName || payment.courseTitle}</p>
                              <p className="text-xs text-gray-500 font-medium capitalize">
                                {payment.itemType === 'plan' ? `${payment.billingCycle} billing` : 'Lifetime Access'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <p className="font-black text-gray-900">{formatAmount(payment.amountPaise)}</p>
                          <p className="text-[10px] text-gray-400 font-bold uppercase">{payment.currency}</p>
                        </td>
                        <td className="px-6 py-5 text-sm font-semibold text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-gray-400" />
                            {formatDate(payment.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[10px] font-black uppercase tracking-wider ${getStatusColor(payment.status, isLive)}`}>
                            {payment.status === 'captured' ? (
                              <>
                                <CheckCircle2 size={12} />
                                Successful
                              </>
                            ) : (
                              <>
                                <XCircle size={12} />
                                Failed
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-5 text-right">
                          {payment.status === 'captured' && (
                            <div className="flex items-center justify-end gap-3">
                              <div className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold ${isLive ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                {isLive ? 'Active' : 'Expired'}
                              </div>
                              <button
                                onClick={() => handleDownloadReceipt(payment)}
                                className="p-2 rounded-xl bg-gray-50 text-gray-400 hover:bg-primary-600 hover:text-white transition-all shadow-sm"
                                title="Download Receipt"
                              >
                                <Download size={16} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-bold">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-bold">{Math.min(currentPage * itemsPerPage, filteredHistory.length)}</span> of <span className="font-bold">{filteredHistory.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft size={16} />
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-bold ${
                          currentPage === i + 1
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight size={16} />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserPaymentHistory;
